import { Button } from "@/components/ui/button";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

export interface FormActionsProps {
  onReset?: () => void;
  submitText?: string;
  resetText?: string;
  submitIcon?: LucideIcon;
  resetIcon?: LucideIcon;
  isSubmitting?: boolean;
  disabled?: boolean;
  hideReset?: boolean;
  children?: ReactNode;
  onClick?: () => void;
}

/**
 * Standard form action buttons (Reset + Submit)
 *
 * @example
 * ```tsx
 * <FormActions
 *   onReset={() => form.reset()}
 *   submitText="Save Changes"
 *   submitIcon={SaveIcon}
 *   isSubmitting={form.formState.isSubmitting}
 * />
 * ```
 */
export function FormActions({
  onReset,
  submitText = "Submit",
  resetText = "Reset",
  submitIcon: SubmitIcon,
  resetIcon: ResetIcon,
  isSubmitting = false,
  disabled = false,
  hideReset = false,
  children,
  onClick,
}: FormActionsProps) {
  return (
    <div className="flex flex-row-reverse items-center justify-end gap-3">
      <Button type={onClick ? "button" : "submit"} disabled={isSubmitting || disabled} onClick={onClick}>
        {SubmitIcon && <SubmitIcon className="mr-2 h-4 w-4" />}
        {isSubmitting ? "Submitting..." : submitText}
      </Button>
      {!hideReset && onReset && (
        <Button type="button" variant="outline" onClick={onReset} disabled={isSubmitting}>
          {ResetIcon && <ResetIcon className="mr-2 h-4 w-4" />}
          {resetText}
        </Button>
      )}
      {children}
    </div>
  );
}

